import * as Yup from 'yup';

import {
  Alert,
  Button,
  Col,
  Container,
  Form,
  Modal,
  Row,
  Spinner,
} from 'react-bootstrap';
import { Field, Formik } from 'formik';
import React, { useState } from 'react';
import { deleteUser, getUserDetails } from '../../api/TrustPower';

import { Auth } from 'aws-amplify';
import CloseButton from '../Styled/CloseButton';
import { ConsentFlows } from '../../context/ConsentContext';
import ErrorLabel from '../../ui/ErrorLabel';
import ReactMarkdown from 'react-markdown';
import checkboxEmpty from '../../assets/svg/check-box-empty.svg';
import checkboxError from '../../assets/svg/check-box-error.svg';
import checkboxFilled from '../../assets/svg/check-box-filled.svg';
import cx from 'classnames';
import exclamationIcon from '../../assets/svg/exclamation-icon.svg';
import modalPropTypes from '../../propTypes/modalPropTypes';
import styled from 'styled-components';

const Description = styled.p`
  font-size: 16px;
  line-height: 1.5;
  letter-spacing: normal;
  color: #0c1f28;
`;

const WarningNote = styled.span`
  font-size: 16px;
  line-height: 1.5;
  letter-spacing: normal;
  color: #ff0085;
  font-weight: 700;
`;

const StyledDiv = styled.div`
  p:last-child {
    margin-bottom: 0;
  }

  ul {
    list-style: none;
    padding-left: 18px;

    li {
      margin-bottom: 8px;

      &::before {
        content: '\\2022';
        color: #ff0085;
        font-weight: bold;
        display: inline-block;
        width: 1em;
        margin-left: -1em;
        font-size: 20px;
      }
    }
  }

  &.terms-div {
    cursor: pointer;
  }

  .form-check {
    padding-left: 0;
    background: url(${checkboxEmpty}) no-repeat top left;

    input {
      display: none;
    }
    .form-check-label {
      p {
        padding-left: 32px;
      }
    }
  }

  &.selected {
    .form-check {
      background-image: url(${checkboxFilled});
    }
  }
  &.error {
    .form-check {
      background-image: url(${checkboxError});
    }
  }

  .error-label {
    margin-left: 32px;
  }
`;

const doNothing = () => {};

const EditAddressModal = ({ show, onHide }) => {
  // Recursively check if the users devices have been removed
  const checkSmartMeter = async () => {
    let userHasDevices = true;
    try {
      await getUserDetails();
    } catch (e) {
      if (e.response.status === 404) {
        userHasDevices = false;
      }
    }

    if (userHasDevices) {
      await new Promise(r => setTimeout(r, 500));
      userHasDevices = checkSmartMeter();
    }

    return userHasDevices;
  };

  const onSubmit = async () => {
    try {
      return await deleteUser(null, true).then(async () => {
        // update Cognito user attributes
        const attributes = {
          address: '',
          'custom:smart_meter_user': 'false',
          'custom:postcode': '',
        };
        const user = await Auth.currentAuthenticatedUser();
        await Auth.updateUserAttributes(user, attributes);

        const localStorageToRemove = [
          'addresses',
          'postcode',
          'mpan',
          'lookedUpMpanKnown',
          'lookedUpMpanObfuscated',
          'lookedUpMpanHash',
          'moveDate',
          'hasRegisterMpanTrustedConsent',
          'hasOnboarded',
          'showManualAddressOption',
        ];
        localStorageToRemove.forEach(key => {
          localStorage.removeItem(key);
        });
        // Poll for account change
        await checkSmartMeter();
        window.location.href = `/${ConsentFlows.ONBOARDING}/address`;
      });
    } catch (error) {
      addAlert('danger', error.message);
    }
  };

  // TODO: make this reusable
  const [showAlert, setShowAlert] = useState(false);
  const [alert, setAlert] = useState(undefined);

  const addAlert = (variant, message) => {
    setShowAlert(true);
    setAlert({ variant, message });
  };

  const hideAlert = () => {
    setShowAlert(false);
    setAlert(undefined);
  };

  return (
    <Formik
      validationSchema={Yup.object().shape({
        confirm: Yup.boolean().oneOf(
          [true],
          'Please check this box before continuing.',
        ),
      })}
      onSubmit={onSubmit}
      initialValues={{
        confirm: false,
      }}
      validateOnChange>
      {({ handleSubmit, isValid, values, isSubmitting }) => (
        <Modal
          size="md"
          aria-labelledby="contained-modal-title-vcenter"
          centered
          show={show}
          onHide={!isSubmitting ? onHide : doNothing}>
          <Modal.Header>
            <CloseButton
              onClick={!isSubmitting ? onHide : doNothing}
              variant={'bordered'}
            />
          </Modal.Header>
          <Modal.Body className="pt-0 pb-4">
            <Form onSubmit={handleSubmit}>
              <Container>
                <Row>
                  <Col md={10} className="mb-4">
                    <img
                      alt="Exclamation Icon"
                      src={exclamationIcon}
                      className="mb-4"
                    />
                    <h2 className="color-dark">
                      Editing your address or smart meter details
                    </h2>
                  </Col>
                </Row>
                <Row>
                  <Col className="mb-2">
                    <Description>
                      You can only access smart meter data for a home you live
                      in. When changing your address, we first remove your
                      access to your current smart meter. You will then go
                      through the steps to add your new address and confirm your
                      right to access the smart meter data.
                    </Description>
                  </Col>
                </Row>
                <Row>
                  <Col className="mb-2">
                    <Description>
                      <WarningNote>PLEASE NOTE:</WarningNote> After editing your
                      address or smart meter details, you&apos;ll need to{' '}
                      <strong>
                        sign out of the Loop app on your mobile device, and then
                        sign back in
                      </strong>
                      , so that these changes can be implemented.
                    </Description>
                  </Col>
                </Row>
                {showAlert && alert && (
                  <Row>
                    <Col className="mb-2">
                      <Alert
                        variant={alert.variant}
                        onClose={() => hideAlert()}>
                        {alert.message}
                        <CloseButton onClick={() => hideAlert()} />
                      </Alert>
                    </Col>
                  </Row>
                )}
                <Row>
                  <Col className="mb-4">
                    <Field name="confirm">
                      {({ field, meta }) => (
                        <StyledDiv
                          className={`text-left ${cx({
                            selected: field.value,
                            error: !!meta.error,
                          })}`}>
                          <Form.Check
                            type="checkbox"
                            name="confirm"
                            id="confirm-check"
                            onChange={field.onChange}
                            onBlur={field.onBlur}
                            className={cx({ error: !!meta.error })}
                            disabled={isSubmitting}
                            label={
                              <ReactMarkdown>
                                I understand that my current smart meter will be
                                disconnected from Loop and I will lose access to
                                all data from this meter.
                              </ReactMarkdown>
                            }
                          />
                          {meta.error && meta.touched && (
                            <ErrorLabel label={meta.error} />
                          )}
                        </StyledDiv>
                      )}
                    </Field>
                  </Col>
                </Row>
                <Row className="mb-2">
                  <Col>
                    <Button
                      className="w-100"
                      type="submit"
                      variant="primary"
                      disabled={!values.confirm || (!isValid && !isSubmitting)}>
                      {isSubmitting ? (
                        <Spinner animation="border" size="sm" />
                      ) : (
                        'Confirm'
                      )}
                    </Button>
                  </Col>
                </Row>
                <Row>
                  <Col>
                    <Button
                      className="w-100"
                      type="button"
                      variant="light"
                      disabled={isSubmitting}
                      onClick={!isSubmitting ? onHide : doNothing}>
                      Cancel
                    </Button>
                  </Col>
                </Row>
              </Container>
            </Form>
          </Modal.Body>
        </Modal>
      )}
    </Formik>
  );
};

EditAddressModal.propTypes = {
  ...modalPropTypes,
};

export default EditAddressModal;
